/**
 * Moteur ArUco pur (sans React) — consommé par la vue scan (ScanMobile)
 * via useArucoScanner.
 *
 * Les cartes élèves sont des marqueurs ArUco 4x4 (dictionnaire 1000, tronqué
 * aux MAX_MARKERS premiers codes = codes distribués en classe). La réponse
 * A/B/C/D est donnée par la ROTATION de la carte (position du coin n°0).
 */

let Detector = null;
export const MAX_MARKERS = 30;
try {
  const mod = require('js-aruco2');
  require('js-aruco2/src/dictionaries/aruco_4x4_1000');
  if (mod.AR?.DICTIONARIES?.ARUCO_4X4_1000) {
    mod.AR.DICTIONARIES.ARUCO_4X4_1000.codeList =
      mod.AR.DICTIONARIES.ARUCO_4X4_1000.codeList.slice(0, MAX_MARKERS);
  }
  Detector = mod.AR?.Detector || mod.Detector || null;
} catch {
  // js-aruco2 non disponible (ex. tests)
}

/**
 * Taille de l'image ANALYSÉE pour une largeur cible (benchmark 2026-09-21) :
 * la caméra reste en 1080p, l'aperçu aussi, seule l'image lue par le
 * détecteur est réduite, à proportions égales. Sans cible, ou avec une cible
 * plus large que la vidéo, l'image native, pixel pour pixel : le comportement
 * d'aujourd'hui. Le coût du détecteur (js-aruco2, JavaScript pur : niveaux
 * de gris, seuil, contours sur toute l'image) est proportionnel au nombre de
 * pixels ; 1280 de grand côté en fait 2,25 fois moins qu'en 1920, 960 en
 * fait 4 fois moins.
 *
 * La cible s'applique au GRAND côté de l'image, l'échelle est plafonnée à 1.
 * Un téléphone tenu droit reçoit 1080 × 1920 : comparer la cible à la
 * largeur (1080) rendait 720p sans effet et 540p presque sans effet
 * (960 × 1707, 20 % de pixels en moins au lieu de 4 fois moins), d'où un
 * « coût fixe » qui n'existait pas (mesures des 21 et 22/09). Portrait
 * 1080 × 1920 → 540 × 960, paysage 1920 × 1080 → 960 × 540 : mêmes pixels,
 * aucun cas spécial téléphone ou tablette.
 */
export const LARGEURS_ANALYSE = [1920, 1280, 960];
/** Le nom que le prof lit : pastille du bandeau et boutons du panneau. */
export const LIBELLES_ANALYSE = { 1920: '1080p', 1280: '720p', 960: '540p' };
export const tailleAnalyse = (videoWidth, videoHeight, largeurCible = null) => {
  if (!videoWidth || !videoHeight) return { width: 0, height: 0, echelle: 1 };
  const grandCote = Math.max(videoWidth, videoHeight);
  if (!largeurCible || largeurCible >= grandCote) return { width: videoWidth, height: videoHeight, echelle: 1 };
  const echelle = largeurCible / grandCote;
  return { width: Math.round(videoWidth * echelle), height: Math.round(videoHeight * echelle), echelle };
};

// Réglage AUTOMATIQUE de la taille analysée (lot C, 2026-09-22). Décision
// prof : la cadence d'abord, la portée se rattrape en se déplaçant (un
// téléphone lent en 540p vaut un Fold en HD : 5 s par question). Tout le
// monde démarre en 720p ; toutes les INTERVALLE_REGLAGE_MS, sur la MÉDIANE
// des dix dernières analyses (au moins MIN_ANALYSES_REGLAGE depuis le dernier
// changement) : au-dessus de descenteMs on descend d'un cran, en dessous de
// monteeMs on monte d'un cran, entre les deux rien. La médiane, pas la
// moyenne : un pic isolé (première analyse après l'ouverture de la caméra,
// pause de nettoyage du navigateur) ne fait pas descendre un bon téléphone,
// il faut que la moitié des analyses soient lentes. Un cran = 2,25 fois plus
// ou moins de pixels ; 150 / 60 = 2,5 > 2,25, donc jamais de va-et-vient : un
// appareil qui descend à 160 ms retombe vers 70, il reste. Le contrôle continu
// suit aussi la surchauffe en cours de séance, qu'un réglage pris une fois au
// démarrage raterait. Mesures du 22/09 : tablette ~180 ms en 1080p, téléphone
// chaud 375 ms en 1080p natif, ~100 ms en 540p.
export const LARGEUR_DEPART = 1280;
export const SEUILS_CADENCE = { descenteMs: 150, monteeMs: 60 };
export const INTERVALLE_REGLAGE_MS = 3000;
export const MIN_ANALYSES_REGLAGE = 5;
/** Médiane d'une liste de nombres (liste vide : 0). */
export const mediane = (xs) => {
  if (!xs.length) return 0;
  const t = [...xs].sort((a, b) => a - b);
  const k = t.length >> 1;
  return t.length % 2 ? t[k] : (t[k - 1] + t[k]) / 2;
};
/**
 * Largeur suivante pour une largeur courante et les durées (ms) des dernières
 * analyses à cette largeur. Pure. Moins de MIN_ANALYSES_REGLAGE durées : on
 * ne décide pas.
 */
export const reglerLargeur = (largeur, durees) => {
  const i = LARGEURS_ANALYSE.indexOf(largeur);
  if (i < 0) return LARGEUR_DEPART;
  if (!durees || durees.length < MIN_ANALYSES_REGLAGE) return largeur;
  const m = mediane(durees);
  if (m > SEUILS_CADENCE.descenteMs && i < LARGEURS_ANALYSE.length - 1) return LARGEURS_ANALYSE[i + 1];
  if (m < SEUILS_CADENCE.monteeMs && i > 0) return LARGEURS_ANALYSE[i - 1];
  return largeur;
};

// Une carte comptée est gelée SCAN_COOLDOWN ms (le temps que le consommateur
// l'inscrive dans scannedCodes : double garde, cf. useArucoScanner).
export const SCAN_COOLDOWN = 400;
// Repos minimal entre deux analyses (cf. useArucoScanner) : le repos réel est
// max(REPOS_MIN_MS, durée de l'analyse précédente).
export const REPOS_MIN_MS = 50;

// ── Décision par lecture (refonte 2026-09-19, diagnostic « vingt secondes ») ──
//
// Avant : TOUTE carte attendait 4 lectures identiques parmi les 6 dernières
// analyses, et son compteur était vidé après 6 analyses sans elle. Le prof
// devait rester sur chaque carte jusqu'au bip : carte par carte, et le temps
// suivait le CARRÉ de l'effectif (40 s par question à 12 élèves, 2 min 40 à
// 24, même salle). Sur le banc du back (18 cartes collées, toutes dans le
// cadre, ancien code), la règle coûtait 4,7 s par question sur un Galaxy Fold
// et 32 s sur un vieux téléphone : elle MULTIPLIE la lenteur du téléphone.
//
// La bibliothèque rend pourtant, par carte, le nombre de cases qu'elle a dû
// corriger (`hammingDistance`), jamais lu jusqu'ici. Et l'orientation se
// mesure sur une seule image. D'où deux vérifications, sur la même lecture :
//
//   MOTIF   distance 0 = les seize cases lues exactement. Avec quatre cases
//           d'écart minimal entre nos trente codes (rotations comprises,
//           mesuré), aucune autre carte n'est possible sur une lecture saine.
//   COIN    le vecteur centre → coin 0 tombe franchement dans son quart, loin
//           des axes qui séparent A/B/C/D. Près d'un axe (carte en losange),
//           la lettre se joue sur un pixel : on ne compte PAS, l'élève reste
//           dans les restants, le prof dit « redresse ». Avant, une telle
//           carte était comptée au hasard par le vote.
//
// Règle en service : le VOTE, 2 lectures identiques parmi les 3 dernières,
// pour toute lecture franche. Jamais une seule image ne compte : un accident
// (flou, doigt, pli, reflet) devrait se reproduire à l'identique sur une
// autre image. Et le compteur ne s'efface plus quand la carte sort du cadre :
// deux lectures identiques à dix secondes d'écart sont deux images
// indépendantes, aussi sûres que deux d'affilée ; une carte vue une fois par
// passage finit par être comptée, le balayage par bloc marche. Si l'élève
// change de réponse, les lectures les plus récentes l'emportent.
//
// VOIE RAPIDE (motif exact + coin franc = comptée à la première lecture) : le
// code existe, derrière VOIE_RAPIDE, FERMÉE (décision 2026-09-19). Ce qu'elle
// achète : une analyse au lieu de deux par carte nette, une seconde par bloc
// sur un téléphone rapide, deux à trois sur un lent. Ce qu'elle coûte : une
// carte abîmée dont la lecture tombe exactement sur un AUTRE de nos trente
// codes (au moins quatre cases précises retournées, bordure intacte, de
// l'ordre d'une lecture abîmée sur mille) attribue une réponse à un élève
// qui n'a pas levé sa carte, sans que personne le voie, et rien ne la
// rattrape : une carte comptée n'est plus relue. Une erreur invisible et
// irrattrapable n'a pas de bon taux. La rouvrir demanderait un taux d'erreur
// brut MESURÉ nul sur des milliers de lectures en classe, pas une estimation.
//
// La tolérance du détecteur (maxHammingDistance: 2) ne bouge pas : la
// bibliothèque compare en STRICT (< 2), donc au plus une case corrigée. C'est
// déjà la marge sûre ; seul le nom du réglage prête à confusion.
export const STABLE_BUFFER_SIZE = 3;
export const STABLE_MAJORITY = 2;
export const VOIE_RAPIDE = false;

// Coin franchement dans son quart : rapport petite/grande composante du
// vecteur centre → coin 0. Carte droite : 1. Carte à 30° : 0,27. Carte en
// losange (45°) : 0. Sous ce seuil (penché entre 40° et 50°) la lettre est
// un tirage : on ne conclut pas. Le bruit de mesure vaut un ou deux degrés,
// la bande fait dix : pas de lettre fausse au bord de la bande, seulement
// « compté » ou « pas compté ».
export const MARGE_ORIENTATION = 0.09;

/** Détecteur configuré, ou null si js-aruco2 est indisponible. */
export function createArucoDetector() {
  if (!Detector) return null;
  return new Detector({
    dictionaryName: 'ARUCO_4X4_1000',
    maxHammingDistance: 2,
  });
}

/**
 * Orientation de la carte : la lettre (quart où tombe le coin n°0) ET si ce
 * quart est franc. `rapport` = petite/grande composante du vecteur
 * centre → coin 0 ; `franche` = rapport ≥ MARGE_ORIENTATION.
 * La lettre est calculée exactement comme avant : la marge s'ajoute, elle ne
 * change pas le quart choisi.
 */
export function mesurerOrientation(corners) {
  const cx = (corners[0].x + corners[1].x + corners[2].x + corners[3].x) / 4;
  const cy = (corners[0].y + corners[1].y + corners[2].y + corners[3].y) / 4;
  const dx = corners[0].x - cx;
  const dy = corners[0].y - cy;

  let reponse;
  if (dx < 0 && dy < 0) reponse = 'A';
  else if (dx > 0 && dy < 0) reponse = 'D';
  else if (dx > 0 && dy > 0) reponse = 'C';
  else reponse = 'B';

  const grande = Math.max(Math.abs(dx), Math.abs(dy));
  const petite = Math.min(Math.abs(dx), Math.abs(dy));
  const rapport = grande > 0 ? petite / grande : 0;

  return { reponse, rapport, franche: rapport >= MARGE_ORIENTATION };
}

/** Réponse A/B/C/D seule (compatibilité). */
export function getReponseFromRotation(corners) {
  return mesurerOrientation(corners).reponse;
}

/**
 * Décision pour UNE lecture d'une carte pas encore comptée. Pure, sans état :
 * testable sans caméra. `compteur` = les lettres des lectures précédentes de
 * cette carte (au plus STABLE_BUFFER_SIZE), jamais de ratés dedans.
 *
 * @param {{reponse:string, franche:boolean, distance:number}} lecture
 *   `distance` = cases corrigées par la bibliothèque (0 ou 1 ; 2+ n'arrive
 *   jamais ici, le détecteur les rejette).
 * @param {string[]} compteur
 * @param {{voieRapide?:boolean}} [options] `voieRapide` vaut VOIE_RAPIDE par
 *   défaut (fermée) ; les tests passent les deux réglages explicitement.
 * @returns {{comptee:boolean, reponse:(string|null), compteur:string[]}}
 *   `compteur` = le nouveau compteur à conserver (vide si comptée).
 */
export function deciderLecture(
  { reponse, franche, distance },
  compteur = [],
  { voieRapide = VOIE_RAPIDE } = {},
) {
  // Losange : aucune lettre n'est en haut. Ni comptée, ni inscrite au
  // compteur (une lecture ambiguë ne vote pas).
  if (!franche) return { comptee: false, reponse: null, compteur };

  // Voie rapide, fermée par défaut (cf. doctrine ci-dessus) : motif exact +
  // coin franc = comptée à la première lecture.
  if (voieRapide && distance === 0) return { comptee: true, reponse, compteur: [] };

  // Le vote : deux lectures identiques parmi les trois dernières. Jamais une
  // seule image (un accident ne se reproduit pas à l'identique), et les
  // lectures les plus récentes l'emportent si l'élève change de réponse.
  const suivant = [...compteur, reponse].slice(-STABLE_BUFFER_SIZE);
  const votes = {};
  for (const r of suivant) votes[r] = (votes[r] || 0) + 1;
  const [meilleure, nb] = Object.entries(votes).sort((a, b) => b[1] - a[1])[0];
  if (nb >= STABLE_MAJORITY) return { comptee: true, reponse: meilleure, compteur: [] };
  return { comptee: false, reponse: null, compteur: suivant };
}

/** Contour + étiquette d'un marqueur sur le canvas de scan. */
/**
 * Contour d'une carte sur le canvas transparent : VERT comptée, JAUNE en cours
 * de lecture. Rien d'autre (épreuve de Musk 2026-09-12) : ni numéro ni réponse
 * en clair (le prof n'en a pas besoin en balayant, et la réponse ne regarde
 * que l'élève), ni point d'orientation (outil de mise au point). Le prénom
 * arrive dans le badge « ✓ Prénom » au moment où la carte est comptée.
 */
export function drawMarkerOverlay(ctx, marker, status) {
  const c = marker.corners;
  ctx.strokeStyle = status === 'already' ? '#22c55e' : '#eab308';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(c[0].x, c[0].y);
  for (let i = 1; i < c.length; i++) ctx.lineTo(c[i].x, c[i].y);
  ctx.closePath();
  ctx.stroke();
}
